import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  House, Package, UploadSimple, CreditCard, Bell, Sparkle,
  UsersThree, ClockCounterClockwise, SignOut, Terminal,
} from "@phosphor-icons/react";

const nav = [
  { to: "/", label: "Overview", icon: House, end: true },
  { to: "/items", label: "Items", icon: Package },
  { to: "/files", label: "Files", icon: UploadSimple },
  { to: "/payments", label: "Payments", icon: CreditCard },
  { to: "/notifications", label: "Notifications", icon: Bell },
  { to: "/ai", label: "AI Assistant", icon: Sparkle },
];

const adminNav = [
  { to: "/admin/users", label: "Users", icon: UsersThree },
  { to: "/admin/activity", label: "Activity", icon: ClockCounterClockwise },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-[#EDEDED]">
      {/* Sidebar */}
      <aside className="w-64 border-r border-[#262626] flex flex-col" data-testid="sidebar">
        <div className="px-6 py-6 border-b border-[#262626] flex items-center gap-2">
          <Terminal size={22} weight="bold" className="text-[#0055FF]" />
          <span className="font-display font-black text-lg tracking-tight">DASHBOARD</span>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-[#71717A] px-3 py-2">
            Workspace
          </div>
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm border-l-2 transition-colors ${
                  isActive
                    ? "bg-[#141414] border-[#0055FF] text-white"
                    : "border-transparent text-[#A1A1AA] hover:text-white hover:bg-[#141414]"
                }`
              }
            >
              <n.icon size={16} weight="bold" />
              {n.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-[#71717A] px-3 py-2 mt-4">
                Admin
              </div>
              {adminNav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 text-sm border-l-2 transition-colors ${
                      isActive
                        ? "bg-[#141414] border-[#0055FF] text-white"
                        : "border-transparent text-[#A1A1AA] hover:text-white hover:bg-[#141414]"
                    }`
                  }
                >
                  <n.icon size={16} weight="bold" />
                  {n.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>
        <div className="border-t border-[#262626] p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-sm bg-[#0055FF] text-white flex items-center justify-center font-semibold text-sm">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{user?.name || user?.email}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#71717A]">{user?.role}</div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full rounded-sm border-[#262626] bg-transparent hover:bg-[#141414] hover:text-white"
            onClick={async () => { await logout(); navigate("/login"); }}
            data-testid="logout-btn"
          >
            <SignOut size={14} weight="bold" className="mr-2" /> Sign out
          </Button>
        </div>
      </aside>
      {/* Main */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
